import React, { useEffect, useRef, useState } from 'react';
import { useLanguage, t } from '../../context/LanguageContext';
import driverPortalService from '../../services/driverPortalService';

// Same simple canvas-signature approach as PortalPodCapturePage.js's SignatureCanvas.
function SignaturePad({ onSigned }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);

  const getPoint = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * (canvas.width / rect.width), y: (src.clientY - rect.top) * (canvas.height / rect.height) };
  };
  const startDraw = (e) => { drawing.current = true; lastPoint.current = getPoint(e, canvasRef.current); e.preventDefault(); };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pt = getPoint(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    ctx.stroke();
    lastPoint.current = pt;
  };
  const endDraw = () => { if (!drawing.current) return; drawing.current = false; onSigned(canvasRef.current.toDataURL('image/png')); };
  const clear = () => { canvasRef.current.getContext('2d').clearRect(0, 0, 560, 180); onSigned(null); };

  return (
    <div>
      <canvas
        ref={canvasRef} width={560} height={180}
        style={{ width: '100%', height: 'auto', border: '1px solid #ced4da', borderRadius: 6, background: '#fff', touchAction: 'none', cursor: 'crosshair' }}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
      />
      <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={clear}>Clear</button>
    </div>
  );
}

export default function PortalDriverAgreementPage() {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sigData, setSigData] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    driverPortalService.getAgreement().then(setData).catch(() => setError('Failed to load agreement.')).finally(() => setLoading(false));
  }, []);

  const handleSign = async () => {
    if (!sigData) { setError('Please sign above before submitting.'); return; }
    if (!agreed) { setError('Please confirm you have read and agree to the terms.'); return; }
    setSaving(true); setError('');
    try {
      await driverPortalService.signAgreement(sigData);
      setSuccess(true);
      setData(d => ({ ...d, signed: true }));
    } catch {
      setError('Failed to save your signature. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-5"><span className="spinner-border spinner-border-sm" /></div>;

  if (data?.signed || success) {
    return (
      <div className="text-center text-muted py-5">
        <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
        <p style={{ fontSize: 14 }}>{t('Agreement signed — you can now accept delivery offers.', 'Acuerdo firmado — ya puedes aceptar entregas.', lang)}</p>
      </div>
    );
  }

  return (
    <div>
      <h6 className="fw-bold mb-3">{t('Driver Agreement', 'Acuerdo de Conductor', lang)}</h6>
      {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
      <div
        className="border rounded p-3 mb-3 bg-light"
        style={{ maxHeight: 320, overflowY: 'auto', fontSize: 12, whiteSpace: 'pre-wrap' }}
      >
        {data?.agreementText}
      </div>
      <div className="form-check mb-3">
        <input type="checkbox" className="form-check-input" id="agree" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
        <label className="form-check-label" htmlFor="agree" style={{ fontSize: 13 }}>
          {t('I have read and agree to the terms above.', 'He leído y acepto los términos anteriores.', lang)}
        </label>
      </div>
      <label className="form-label" style={{ fontSize: 13 }}>{t('Sign below', 'Firme abajo', lang)}</label>
      <SignaturePad onSigned={setSigData} />
      <button className="btn btn-primary w-100 mt-3" onClick={handleSign} disabled={saving}>
        {saving ? <span className="spinner-border spinner-border-sm" /> : t('Sign Agreement', 'Firmar Acuerdo', lang)}
      </button>
    </div>
  );
}
